import { DOCUMENT_KINDS, DOCUMENT_SECTIONS, FONT_SCALE, LOGO_POSITION, PAPER_SIZE, WATERMARK_SCOPE, } from './catalog.js';
// ── Valores por defecto ─────────────────────────────────────────────────────
/** Cargo con que comparece por defecto quien firma por la institución. */
export const DEFAULT_REPRESENTATIVE_POSITION = 'Representante legal';
/** En las cooperativas firma usualmente la gerencia general (Decreto 82-78). */
const COOPERATIVE_REPRESENTATIVE_POSITION = 'Gerente general';
/** Nota por defecto al pie del recibo. */
export const DEFAULT_RECEIPT_FOOTER_NOTE = 'Gracias por su pago puntual. Conserve este recibo.';
/** Nota al pie del recibo por tipo de institución (el tono cambia con la relación con el cliente). */
const RECEIPT_FOOTER_NOTES = {
    microfinance_entity: DEFAULT_RECEIPT_FOOTER_NOTE,
    microfinance_ngo: DEFAULT_RECEIPT_FOOTER_NOTE,
    cooperative: 'Gracias por su pago puntual: su cumplimiento fortalece a la cooperativa. Conserve este recibo.',
    finance_company: 'Conserve este recibo como comprobante de su pago; cualquier reclamo debe presentarse con él.',
    private_lender: DEFAULT_RECEIPT_FOOTER_NOTE,
};
/**
 * Cláusula sugerida para cooperativas: compensación con aportaciones, práctica
 * habitual respaldada por los estatutos. Es un punto de partida editable que
 * la cooperativa debe validar con su asesor jurídico.
 */
const COOPERATIVE_CONTRIBUTIONS_CLAUSE = {
    id: 'aportaciones',
    title: 'Aportaciones y compensación',
    body: '{{cliente.nombre}} declara conocer que, conforme a los estatutos de {{institucion.razon_social}}, ' +
        'sus aportaciones y ahorros podrán compensarse con los saldos vencidos de este préstamo.',
};
/** Título de la constancia de cancelación en cooperativas, donde se acostumbra «solvencia». */
const COOPERATIVE_CERTIFICATE_TITLE = 'Constancia de solvencia';
/** Todas las secciones opcionales de un documento, visibles. */
export function allSectionsVisible(kind) {
    const keys = DOCUMENT_SECTIONS[kind];
    return Object.fromEntries(keys.map((key) => [key, true]));
}
/** Opciones por defecto de un documento: título original, sin textos extra y todo visible. */
export function defaultDocumentTemplate(kind) {
    return { title: '', introText: '', footerNote: '', sections: allSectionsVisible(kind), extraSignatures: [], copies: [] };
}
/** Opciones por defecto de todos los documentos. */
function defaultTemplates() {
    return {
        loanContract: { ...defaultDocumentTemplate('loanContract'), extraClauses: [] },
        paymentPlan: defaultDocumentTemplate('paymentPlan'),
        paymentReceipt: defaultDocumentTemplate('paymentReceipt'),
        accountStatement: defaultDocumentTemplate('accountStatement'),
        paidOffCertificate: defaultDocumentTemplate('paidOffCertificate'),
        disbursementVoucher: defaultDocumentTemplate('disbursementVoucher'),
        cashClosing: defaultDocumentTemplate('cashClosing'),
        tabularReport: defaultDocumentTemplate('tabularReport'),
    };
}
/**
 * Personalización inicial según el tipo de institución: la cooperativa firma
 * por gerencia, llama «solvencia» a la constancia y propone la cláusula de
 * aportaciones; la financiera pide conservar el recibo para reclamos. Todo es editable.
 */
export function defaultDocumentSettings(type) {
    const templates = defaultTemplates();
    templates.paymentReceipt.footerNote = RECEIPT_FOOTER_NOTES[type];
    const isCooperative = type === 'cooperative';
    if (isCooperative) {
        templates.loanContract.extraClauses = [{ ...COOPERATIVE_CONTRIBUTIONS_CLAUSE }];
        templates.paidOffCertificate.title = COOPERATIVE_CERTIFICATE_TITLE;
    }
    return {
        appearance: {
            accentColor: null,
            showLogo: true,
            logoPosition: LOGO_POSITION.left,
            fontScale: FONT_SCALE.normal,
            paperSize: PAPER_SIZE.letter,
            headerLines: [],
            footerText: '',
            showPageNumbers: true,
            watermarkText: '',
            watermarkScope: WATERMARK_SCOPE.copies,
        },
        legal: {
            representativeName: '',
            representativeCui: '',
            representativePosition: isCooperative ? COOPERATIVE_REPRESENTATIVE_POSITION : DEFAULT_REPRESENTATIVE_POSITION,
            representationCredential: '',
            institutionRegistration: '',
            signingPlace: '',
            jurisdiction: '',
        },
        templates,
    };
}
/**
 * Completa una configuración guardada con los valores por defecto, campo por
 * campo: así un ajuste que se agregue en una versión nueva tiene un valor
 * razonable antes de que el administrador lo guarde.
 */
export function withDocumentDefaults(saved, type) {
    const defaults = defaultDocumentSettings(type);
    const templates = { ...defaults.templates };
    for (const kind of DOCUMENT_KINDS) {
        const base = defaults.templates[kind];
        const stored = saved?.templates?.[kind];
        templates[kind] = { ...base, ...stored, sections: { ...base.sections, ...stored?.sections } };
    }
    return {
        appearance: { ...defaults.appearance, ...saved?.appearance },
        legal: { ...defaults.legal, ...saved?.legal },
        templates: templates,
    };
}
//# sourceMappingURL=settings.js.map